// MARK://dhk8/pos/features/petty-cash/ReconcileForm
// FASE 2 del ciclo petty-cash (rediseño 2-fases): formulario de CONCILIACIÓN de un
// retiro-compra ya abierto (ver CashoutPage — FASE 1 — y PettyCashDetailPage, que
// monta este componente). Extraído del antiguo `PurchaseForm` embebido en
// CashoutPage (retiro + compra en un solo paso, hoy partido en 2) — ahora vive
// standalone porque la conciliación puede ocurrir en OTRA vista, otro turno u
// otro cajero, sobre un PettyCashPurchase que ya está OPEN.
//
// Este componente es DUEÑO de su propio estado (líneas, adjuntos, proveedor,
// cambio) y de la subida de soportes (uploadMedia, con retry-safe: un adjunto ya
// subido conserva su `key` y no se re-sube si el submit se reintenta). Expone un
// único `onSubmit(payload)` con los datos YA listos para pasar a
// `reconcilePettyCashPurchase(id, payload)` — el padre (PettyCashDetailPage)
// orquesta el resto del ciclo: crear el CASH_PICKUP del cambio (si aplica) ANTES
// de llamar a reconcile, y mantener el `idempotencyKey` estable entre reintentos.
//
// Identidad de dinero preservada IDÉNTICA al PurchaseForm original (misma fórmula,
// mismo bloqueo de submit si no cuadra — el servidor la re-valida con 422):
//   retirado − cambio + aporte === gastado (Σ líneas)
//
// Decisión de producto (rediseño 2-fases): se ELIMINA el picker de almacén de este
// formulario — el almacén de las líneas capitalizables lo fija el stock item, ya
// no se captura aquí.

import { useEffect, useMemo, useState } from 'react'
import { Paperclip, Plus, Trash2, X } from 'lucide-react'

import { formatCents, pesosStringToCents } from '../../lib/money.js'
import { uploadMedia } from '../../lib/clients/media.js'
import type { PettyCashAttachmentInput, PettyCashLineInput } from '../../lib/clients/petty-cash.js'
import {
  listStockItems, listSuppliers, type CatalogStockItem, type CatalogSupplier,
} from '../../lib/clients/inventory-catalog.js'
import { HelpTip } from '../../components/HelpTip.js'
import { SearchSelect, type SearchSelectOption } from '../../components/SearchSelect.js'

// ── Borrador de línea / adjunto (mismo shape que el PurchaseForm original) ────

interface PurchaseLineDraft {
  id: string
  label: string
  capitalizable: boolean
  qtyStr: string
  unitCostStr: string
  totalStr: string
  totalEdited: boolean
  stockItemId?: string
  productId?: string
  unit?: string
}

interface AttachmentDraft {
  id: string
  file: File
  key?: string
  error?: string
}

function newLineDraft(): PurchaseLineDraft {
  // Default capitalizable=true: el caso primario de petty-cash en el café es
  // comprar insumo que entra a inventario (StockLot a costo real).
  return { id: rid('line'), label: '', capitalizable: true, qtyStr: '1', unitCostStr: '', totalStr: '', totalEdited: false }
}

function parseQty(qtyStr: string): number {
  const n = Number.parseFloat(qtyStr.replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : 0
}

function lineUnitCostCents(l: PurchaseLineDraft): number {
  return Math.max(0, pesosStringToCents(l.unitCostStr))
}

/** Total autocalculado (qty × costo unitario) en centavos. */
function lineAutoTotalCents(l: PurchaseLineDraft): number {
  return Math.max(0, Math.round(parseQty(l.qtyStr) * lineUnitCostCents(l)))
}

/** Total efectivo de la línea: el editado a mano gana sobre el autocalculado. */
function lineTotalCents(l: PurchaseLineDraft): number {
  return l.totalEdited ? Math.max(0, pesosStringToCents(l.totalStr)) : lineAutoTotalCents(l)
}

function rid(prefix: string): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  return c?.randomUUID ? `${prefix}_${c.randomUUID().slice(0, 8)}` : `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

// ── Payload que recibe el padre (ya listo para reconcilePettyCashPurchase) ────

export interface ReconcileSubmitPayload {
  lines: PettyCashLineInput[]
  changeCents: number
  cashierContributedCents: number
  supplierId?: string
  notes?: string
  attachments: PettyCashAttachmentInput[]
}

export function ReconcileForm({
  withdrawnCents,
  onSubmit,
  disabled,
}: {
  /** Efectivo ya retirado (fijo — viene del CASH_DROP de la FASE 1; este
   *  formulario NO lo recaptura, sólo lo usa para la identidad de dinero). */
  withdrawnCents: number
  /** El padre orquesta el resto del ciclo (pickup del cambio + la llamada real
   *  al backend con idempotencyKey estable). Un throw aquí se muestra inline. */
  onSubmit: (payload: ReconcileSubmitPayload) => Promise<void>
  /** Deshabilita el formulario mientras el padre hace su parte del submit
   *  (p.ej. crear el CASH_PICKUP del cambio antes de llamar a reconcile). */
  disabled?: boolean
}) {
  const [lines, setLines] = useState<PurchaseLineDraft[]>(() => [newLineDraft()])
  const [attachments, setAttachments] = useState<AttachmentDraft[]>([])
  // Proveedor: texto visible (supplierText) + id resuelto (supplierId, sólo si
  // se eligió de la lista). Texto libre sin match → queda en notas, NO se inventa id.
  const [supplierText, setSupplierText] = useState('')
  const [supplierId, setSupplierId] = useState<string | null>(null)
  const [stockItems, setStockItems] = useState<CatalogStockItem[]>([])
  const [suppliers, setSuppliers] = useState<CatalogSupplier[]>([])
  // Arranca en `true` (en vez de un `setCatalogLoading(true)` síncrono al
  // entrar al efecto de abajo) — sólo el `false` final ocurre dentro del
  // callback async de la promesa.
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [changeStr, setChangeStr] = useState('')
  const [changeEdited, setChangeEdited] = useState(false)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  // Precarga de catálogos (inventario + proveedores). Ante error/permiso
  // devuelven [] → cada campo degrada a texto libre (no crítico del money-path).
  useEffect(() => {
    const ctrl = new AbortController()
    Promise.all([listStockItems(ctrl.signal), listSuppliers(ctrl.signal)])
      .then(([si, sup]) => { setStockItems(si); setSuppliers(sup) })
      .finally(() => setCatalogLoading(false))
    return () => ctrl.abort()
  }, [])

  // ── Dinero de la compra (idéntico al PurchaseForm original) ───────────────
  const gastadoCents = lines.reduce((s, l) => s + lineTotalCents(l), 0)
  const autoChangeCents = Math.max(0, withdrawnCents - gastadoCents)
  const cambioCents = changeEdited ? Math.max(0, pesosStringToCents(changeStr)) : autoChangeCents
  const aporteCents = Math.max(0, gastadoCents - withdrawnCents)
  // Identidad que también valida el backend (422 si no cuadra).
  const cuadra = withdrawnCents - cambioCents + aporteCents === gastadoCents
  const linesValid = lines.length > 0 && lines.every((l) => l.label.trim().length > 0 && lineTotalCents(l) > 0)
  const ready = linesValid && cuadra
  const isBusy = busy || Boolean(disabled)

  function patchLine(id: string, patch: Partial<PurchaseLineDraft>): void {
    setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }
  function addLine(): void { setLines((ls) => [...ls, newLineDraft()]) }
  function removeLine(id: string): void { setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.id !== id) : ls)) }

  function onPickFiles(fileList: FileList | null): void {
    const files = Array.from(fileList ?? [])
    if (files.length === 0) return
    setAttachments((a) => [...a, ...files.map((f) => ({ id: rid('att'), file: f }))])
  }
  function removeAttachment(id: string): void { setAttachments((a) => a.filter((x) => x.id !== id)) }

  const itemOptions: SearchSelectOption[] = useMemo(() => stockItems.map((s) => ({
    value: s.stockItemId,
    label: s.name,
    hint: [s.unit, s.unitCostCents != null ? formatCents(s.unitCostCents) : null].filter(Boolean).join(' · ') || undefined,
  })), [stockItems])
  const supplierOptions: SearchSelectOption[] = useMemo(() => suppliers.map((s) => ({
    value: s.id, label: s.name, ...(s.rfc ? { hint: s.rfc } : {}),
  })), [suppliers])

  // Elegir un insumo de inventario → autollena label/unidad/costo, liga
  // stockItemId + productId y marca capitalizable (entra a existencias).
  function selectItemForLine(lineId: string, opt: SearchSelectOption): void {
    const it = stockItems.find((s) => s.stockItemId === opt.value)
    if (!it) { patchLine(lineId, { label: opt.label, stockItemId: undefined, productId: undefined, unit: undefined }); return }
    patchLine(lineId, {
      label: it.name,
      stockItemId: it.stockItemId,
      capitalizable: true,
      ...(it.productId ? { productId: it.productId } : { productId: undefined }),
      ...(it.unit ? { unit: it.unit } : { unit: undefined }),
      ...(it.unitCostCents != null ? { unitCostStr: (it.unitCostCents / 100).toFixed(2), totalEdited: false } : {}),
    })
  }
  // Texto libre en la línea → deja de ser un match exacto (ítem no inventariado).
  function typeLineLabel(lineId: string, text: string): void {
    patchLine(lineId, { label: text, stockItemId: undefined, productId: undefined, unit: undefined })
  }

  // ── Submit: sube adjuntos pendientes (retry-safe) y entrega el payload ────
  async function handleSubmit(): Promise<void> {
    if (!linesValid) { setLocalError('Cada línea necesita descripción y un total mayor a $0'); return }
    if (!cuadra) { setLocalError('El desglose no cuadra: retirado − cambio + aporte debe igualar lo gastado'); return }
    setBusy(true); setLocalError(null)
    try {
      const uploaded: PettyCashAttachmentInput[] = []
      for (const att of attachments) {
        let key = att.key
        if (!key) {
          try {
            const r = await uploadMedia(att.file, 'petty-cash')
            key = r.key
            setAttachments((a) => a.map((x) => (x.id === att.id ? { ...x, key: r.key, error: undefined } : x)))
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'error al subir'
            setAttachments((a) => a.map((x) => (x.id === att.id ? { ...x, error: msg } : x)))
            throw new Error(`No se pudo subir un soporte (${att.file.name}): ${msg}`, { cause: e })
          }
        }
        uploaded.push({
          storageKey: key,
          contentType: att.file.type || 'application/octet-stream',
          ...(supplierId ? { supplierId } : {}),
        })
      }
      const purchaseLines: PettyCashLineInput[] = lines.map((l) => ({
        label: l.label.trim(),
        qty: parseQty(l.qtyStr),
        unitCostCents: lineUnitCostCents(l),
        totalCents: lineTotalCents(l),
        capitalizable: l.capitalizable,
        // Elegido de inventario → match exacto (stockItemId + productId).
        ...(l.stockItemId ? { stockItemId: l.stockItemId } : {}),
        ...(l.productId ? { productId: l.productId } : {}),
      }))
      // Proveedor de texto libre (sin match) NO se manda como supplierId (no se
      // inventa un id): se anota en las notas de la compra para trazabilidad.
      const freeSupplierNote = !supplierId && supplierText.trim() ? `Proveedor: ${supplierText.trim()}` : ''
      const purchaseNotes = [notes.trim(), freeSupplierNote].filter(Boolean).join(' · ')
      await onSubmit({
        lines: purchaseLines,
        changeCents: cambioCents,
        cashierContributedCents: aporteCents,
        ...(supplierId ? { supplierId } : {}),
        ...(purchaseNotes ? { notes: purchaseNotes } : {}),
        attachments: uploaded,
      })
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'No se pudo conciliar la compra')
    } finally {
      setBusy(false)
    }
  }

  const identityCents = withdrawnCents - cambioCents + aporteCents

  return (
    <div className="dhk8-petty">
      {/* ── Líneas: qué compraste ─────────────────────────────────────────── */}
      <div className="dhk8-petty__section">
        <div className="dhk8-petty__section-head">
          <span>¿Qué compraste?</span>
          <button type="button" className="dhk8-petty__add" onClick={addLine} disabled={isBusy}>
            <Plus size={14} /> Línea
          </button>
        </div>
        <div className="dhk8-petty__lines">
          {lines.map((l) => {
            const autoTotal = lineAutoTotalCents(l)
            return (
              <div className="dhk8-petty__line" key={l.id}>
                <SearchSelect
                  value={l.label}
                  options={itemOptions}
                  onSelect={(opt) => selectItemForLine(l.id, opt)}
                  onText={(text) => typeLineLabel(l.id, text)}
                  placeholder="¿Qué compraste? (elige del inventario o escribe)"
                  loading={catalogLoading}
                  emptyText={itemOptions.length === 0 ? 'Sin inventario disponible — escribe la descripción' : 'Sin coincidencias — se usará el texto tal cual'}
                  disabled={isBusy}
                />
                {l.stockItemId && <span className="dhk8-petty__line-tag">✓ inventariado</span>}
                <div className="dhk8-petty__line-nums">
                  <label className="dhk8-petty__num">
                    <span>Cant.{l.unit ? ` (${l.unit})` : ''}</span>
                    <input inputMode="decimal" value={l.qtyStr} placeholder="1"
                      onChange={(e) => patchLine(l.id, { qtyStr: e.target.value })} disabled={isBusy} />
                  </label>
                  <label className="dhk8-petty__num">
                    <span>Costo unit.</span>
                    <input inputMode="decimal" value={l.unitCostStr} placeholder="0.00"
                      onChange={(e) => patchLine(l.id, { unitCostStr: e.target.value })} disabled={isBusy} />
                  </label>
                  <label className="dhk8-petty__num">
                    <span>Total</span>
                    <input inputMode="decimal"
                      value={l.totalEdited ? l.totalStr : (autoTotal / 100).toFixed(2)}
                      onChange={(e) => patchLine(l.id, { totalStr: e.target.value, totalEdited: true })}
                      disabled={isBusy} />
                  </label>
                </div>
                <div className="dhk8-petty__line-foot">
                  <label className="dhk8-petty__toggle">
                    <input type="checkbox" checked={l.capitalizable}
                      onChange={(e) => patchLine(l.id, { capitalizable: e.target.checked })} disabled={isBusy} />
                    ¿Va a inventario?
                    <HelpTip text="Actívalo si lo comprado es insumo/producto que entra a existencias (se registra como StockLot a costo real, en el almacén del stock item). Apágalo para papelería o gasto operativo (se registra como gasto)." />
                  </label>
                  <button type="button" className="dhk8-petty__rm" aria-label="Quitar línea"
                    onClick={() => removeLine(l.id)} disabled={isBusy || lines.length <= 1}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Proveedor ─────────────────────────────────────────────────────── */}
      <div className="dhk8-cashcut-field">
        <label>Proveedor (opcional)</label>
        <SearchSelect
          value={supplierText}
          options={supplierOptions}
          onSelect={(opt) => { setSupplierId(opt.value); setSupplierText(opt.label) }}
          onText={(v) => { setSupplierText(v); setSupplierId(null) }}
          placeholder="Elige un proveedor o escribe el nombre"
          loading={catalogLoading}
          emptyText={supplierOptions.length === 0 ? 'Sin proveedores registrados — escribe el nombre' : 'Sin coincidencias — se guardará el nombre en notas'}
          disabled={isBusy}
        />
      </div>

      {/* ── Soportes (fotos / PDF / XML) ──────────────────────────────────── */}
      <div className="dhk8-petty__section">
        <div className="dhk8-petty__section-head">
          <span>Soportes (opcional)</span>
          <label className="dhk8-petty__add">
            <Paperclip size={14} /> Adjuntar
            <input type="file" multiple accept="image/*,application/pdf,.xml"
              style={{ display: 'none' }}
              onChange={(e) => { onPickFiles(e.target.files); e.target.value = '' }}
              disabled={isBusy}
            />
          </label>
        </div>
        {attachments.length > 0 && (
          <ul className="dhk8-petty__files">
            {attachments.map((a) => (
              <li key={a.id} className={`dhk8-petty__file${a.error ? ' dhk8-petty__file--err' : ''}`}>
                <span className="dhk8-petty__file-name">
                  {a.file.name}{a.key ? ' ✓' : a.error ? ` · ${a.error}` : ''}
                </span>
                <button type="button" aria-label="Quitar soporte"
                  onClick={() => removeAttachment(a.id)} disabled={isBusy}>
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="dhk8-cashcut-hint">Fotos del ticket, PDF o XML de la factura. Todos opcionales.</p>
      </div>

      {/* ── Notas ───────────────────────────────────────────────────────────── */}
      <div className="dhk8-cashcut-field">
        <label>Notas (opcional)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
          placeholder="Detalle de la compra, quién autorizó…" disabled={isBusy} />
      </div>

      {/* ── Resumen de dinero ─────────────────────────────────────────────── */}
      <div className="dhk8-petty__money">
        <div className="dhk8-petty__money-row">
          <span>Retirado del cajón</span><strong>{formatCents(withdrawnCents)}</strong>
        </div>
        <div className="dhk8-petty__money-row">
          <span>Gastado (Σ líneas)</span><strong>{formatCents(gastadoCents)}</strong>
        </div>
        <div className="dhk8-petty__money-row">
          <span>Cambio devuelto al cajón</span>
          <span className="dhk8-petty__change">
            <input inputMode="decimal"
              value={changeEdited ? changeStr : (autoChangeCents / 100).toFixed(2)}
              onChange={(e) => { setChangeStr(e.target.value); setChangeEdited(true) }} disabled={isBusy} />
            {changeEdited && (
              <button type="button" className="dhk8-petty__reset"
                onClick={() => { setChangeEdited(false); setChangeStr('') }} disabled={isBusy}>auto</button>
            )}
          </span>
        </div>
        {aporteCents > 0 && (
          <div className="dhk8-petty__money-row dhk8-petty__money-row--contrib">
            <span>Aporté de mi bolsa</span><strong>{formatCents(aporteCents)}</strong>
          </div>
        )}
        <div className={`dhk8-petty__identity${cuadra ? ' dhk8-petty__identity--ok' : ' dhk8-petty__identity--bad'}`}>
          {cuadra
            ? '✓ Cuadra (retirado − cambio + aporte = gastado)'
            : `No cuadra: ${formatCents(identityCents)} ≠ gastado ${formatCents(gastadoCents)}`}
        </div>
      </div>

      {localError && <div className="dhk8-cashcut-error">{localError}</div>}

      <div className="dhk8-petty__actions">
        <button type="button" className="dhk8-petty__submit" disabled={isBusy || !ready} onClick={() => void handleSubmit()}>
          {isBusy ? 'Conciliando…' : `Conciliar compra ${formatCents(gastadoCents)}`}
        </button>
      </div>

      <style>{RECONCILE_FORM_STYLES}</style>
    </div>
  )
}

// ── Styles (self-contained: este componente se monta fuera de CashoutPage) ──
const RECONCILE_FORM_STYLES = `
  .dhk8-cashcut-field { display:flex; flex-direction:column; gap:var(--space-1); }
  .dhk8-cashcut-field label { font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-cashcut-field input,.dhk8-cashcut-field textarea { border:1px solid var(--separator-opaque); padding:var(--space-2) var(--space-3); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); outline:none; width:100%; border-radius:var(--radius-sm); }
  .dhk8-cashcut-hint { margin:0; font-family:var(--font-mono); font-size:var(--text-xxs); color:var(--text-tertiary); }
  .dhk8-cashcut-error { padding:var(--space-2) var(--space-3); background:var(--status-error-bg); border:1px solid var(--status-error); color:var(--status-error); font-family:var(--font-mono); font-size:var(--text-xs); border-radius:var(--radius-sm); }

  .dhk8-petty { display:flex; flex-direction:column; gap:var(--space-4); padding:var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-canvas); }
  .dhk8-petty__section { display:flex; flex-direction:column; gap:var(--space-2); }
  .dhk8-petty__section-head { display:flex; justify-content:space-between; align-items:center; font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-petty__add { display:inline-flex; align-items:center; gap:var(--space-1); min-height:36px; padding:var(--space-1) var(--space-3); border:1px solid var(--separator-opaque); background:var(--bg-surface); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-xs); cursor:pointer; border-radius:var(--radius-pill); }
  .dhk8-petty__add:hover:not(:disabled) { background:var(--bg-elevated); }
  .dhk8-petty__add:disabled { opacity:.5; cursor:not-allowed; }

  .dhk8-petty__lines { display:flex; flex-direction:column; gap:var(--space-3); }
  .dhk8-petty__line { display:flex; flex-direction:column; gap:var(--space-2); padding:var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); }
  .dhk8-petty__line-tag { align-self:flex-start; font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.06em; text-transform:uppercase; color:var(--tint); border:1px solid var(--tint); padding:1px 6px; border-radius:var(--radius-pill); }
  .dhk8-petty__line-nums { display:grid; grid-template-columns:repeat(3, 1fr); gap:var(--space-2); }
  @media (max-width:520px) { .dhk8-petty__line-nums { grid-template-columns:1fr; } }
  .dhk8-petty__num { display:flex; flex-direction:column; gap:var(--space-1); }
  .dhk8-petty__num span { font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.06em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-petty__num input { border:1px solid var(--separator-opaque); padding:var(--space-2) var(--space-3); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); font-variant-numeric:tabular-nums; outline:none; width:100%; border-radius:var(--radius-sm); min-height:44px; text-align:right; }
  .dhk8-petty__line-foot { display:flex; justify-content:space-between; align-items:center; gap:var(--space-2); }
  .dhk8-petty__toggle { display:inline-flex; align-items:center; gap:var(--space-2); font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-secondary); cursor:pointer; user-select:none; min-height:44px; }
  .dhk8-petty__toggle input { width:20px; height:20px; cursor:pointer; }
  .dhk8-petty__rm { width:44px; height:44px; display:flex; align-items:center; justify-content:center; border:1px solid var(--separator-opaque); background:transparent; color:var(--text-tertiary); cursor:pointer; border-radius:var(--radius-sm); }
  .dhk8-petty__rm:hover:not(:disabled) { color:var(--pos-danger,#E5484D); border-color:var(--pos-danger,#E5484D); }
  .dhk8-petty__rm:disabled { opacity:.3; cursor:not-allowed; }

  .dhk8-petty__files { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:var(--space-1); }
  .dhk8-petty__file { display:flex; justify-content:space-between; align-items:center; gap:var(--space-2); padding:var(--space-1) var(--space-2); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-secondary); }
  .dhk8-petty__file--err { border-color:var(--pos-danger,#E5484D); color:var(--pos-danger,#E5484D); }
  .dhk8-petty__file-name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .dhk8-petty__file button { width:32px; height:32px; display:flex; align-items:center; justify-content:center; border:none; background:transparent; color:inherit; cursor:pointer; }

  .dhk8-petty__money { display:flex; flex-direction:column; gap:var(--space-2); padding:var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); }
  .dhk8-petty__money-row { display:flex; justify-content:space-between; align-items:center; gap:var(--space-3); font-family:var(--font-mono); font-size:var(--text-sm); color:var(--text-secondary); }
  .dhk8-petty__money-row strong { font-variant-numeric:tabular-nums; color:var(--text-primary); }
  .dhk8-petty__money-row--contrib { color:var(--status-warning); }
  .dhk8-petty__money-row--contrib strong { color:var(--status-warning); }
  .dhk8-petty__change { display:inline-flex; align-items:center; gap:var(--space-2); }
  .dhk8-petty__change input { width:120px; border:1px solid var(--separator-opaque); padding:var(--space-1) var(--space-2); background:var(--bg-canvas); color:var(--text-primary); font-family:var(--font-mono); font-size:var(--text-sm); font-variant-numeric:tabular-nums; text-align:right; outline:none; border-radius:var(--radius-sm); min-height:40px; }
  .dhk8-petty__reset { min-height:32px; padding:0 var(--space-2); border:1px solid var(--separator-opaque); background:transparent; color:var(--text-tertiary); font-family:var(--font-mono); font-size:var(--text-xxs); cursor:pointer; border-radius:var(--radius-pill); }
  .dhk8-petty__identity { padding:var(--space-2) var(--space-3); border-radius:var(--radius-sm); font-family:var(--font-mono); font-size:var(--text-xs); border:1px dashed var(--separator-opaque); }
  .dhk8-petty__identity--ok { color:var(--tint); border-color:var(--tint); }
  .dhk8-petty__identity--bad { color:var(--status-error); border-color:var(--status-error); background:var(--status-error-bg); }

  .dhk8-petty__actions { display:flex; justify-content:flex-end; }
  .dhk8-petty__submit { min-height:48px; padding:var(--space-3) var(--space-5); border:1px solid var(--tint); background:var(--tint); color:var(--bg-canvas); font-family:var(--font-mono); font-size:var(--text-base); font-weight:600; cursor:pointer; border-radius:var(--radius-sm); }
  .dhk8-petty__submit:disabled { opacity:.5; cursor:not-allowed; }
`
